import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, Button, TextField, Rating, Paper, CircularProgress } from '@mui/material'
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firebase_firestore } from '../common/firebaseConfig'

interface TourFeedbackProps {
  tourType: 'ALERT' | 'PATTERN' | 'BACKTEST'
  onSubmit?: () => void
  gcid?: string
  tradingHouse?: string
}

interface FeedbackData {
  type: 'ALERT' | 'PATTERN' | 'BACKTEST'
  date: Timestamp
  rating: number
  message?: string
  gcid: string
  tradingHouse: string
}

const TourFeedback: React.FC<TourFeedbackProps> = ({
  tourType,
  onSubmit,
  gcid = 'anonymous',
  tradingHouse = 'unknown',
}) => {
  const [rating, setRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string>('')
  const [showFeedbackField, setShowFeedbackField] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [documentRef, setDocumentRef] = useState<any>(null)

  // Find existing review from this user for this tour type less than 1 month old
  const findRecentReview = useCallback(async (): Promise<QueryDocumentSnapshot | null> => {
    if (!gcid || gcid === 'anonymous') return null

    try {
      const oneMonthAgo = new Date()
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
      const oneMonthAgoTimestamp = Timestamp.fromDate(oneMonthAgo)

      const feedbackRef = collection(firebase_firestore, 'feedback')
      const q = query(feedbackRef, where('gcid', '==', gcid), where('type', '==', tourType))

      const querySnapshot = await getDocs(q)

      let mostRecentDoc: QueryDocumentSnapshot | null = null
      let mostRecentTimestamp = Timestamp.fromDate(new Date(0)) // epoch start

      querySnapshot.forEach((doc) => {
        const data = doc.data()
        const reviewTimestamp = data.date as Timestamp

        // Check if this review is newer than our current most recent
        if (
          reviewTimestamp.toMillis() > mostRecentTimestamp.toMillis() &&
          reviewTimestamp.toMillis() >= oneMonthAgoTimestamp.toMillis()
        ) {
          mostRecentDoc = doc
          mostRecentTimestamp = reviewTimestamp
        }
      })

      return mostRecentDoc
    } catch (error) {
      console.error('Error finding recent review:', error)
      return null
    }
  }, [gcid, tourType])

  // Load existing feedback on component mount
  useEffect(() => {
    const loadExistingFeedback = async () => {
      const existingReview = await findRecentReview()
      if (existingReview) {
        const data = existingReview.data()

        // Pre-populate the form with existing data
        if (data.rating) {
          setRating(data.rating)
        }
        if (data.message) {
          setFeedback(data.message)
          setShowFeedbackField(true) // Show the feedback field if there's an existing message
        }

        // Set the document reference for future updates
        const docRef = doc(firebase_firestore, 'feedback', existingReview.id)
        setDocumentRef(docRef)
      }
    }

    loadExistingFeedback()
  }, [findRecentReview])

  // Show feedback field automatically when rating exists
  useEffect(() => {
    if (rating !== null) {
      setShowFeedbackField(true)
    }
  }, [rating])

  // Handle rating selection
  const handleRatingChange = async (event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue === null) return

    setRating(newValue)
    setShowFeedbackField(true)

    try {
      // If we already have a document reference (from existing feedback), update it
      if (documentRef) {
        await updateDoc(documentRef, {
          rating: newValue,
          date: Timestamp.now(),
        })
        return
      }

      // Otherwise, check for recent review or create new one
      const recentReview = await findRecentReview()

      if (recentReview) {
        // Update existing review
        const docRef = doc(firebase_firestore, 'feedback', recentReview.id)
        setDocumentRef(docRef)
        await updateDoc(docRef, {
          rating: newValue,
          date: Timestamp.now(),
        })
      } else {
        // Create a new review
        const feedbackData: FeedbackData = {
          type: tourType,
          date: Timestamp.now(),
          rating: newValue,
          gcid,
          tradingHouse,
        }

        const tourFeedbackRef = collection(firebase_firestore, 'feedback')
        const newDocRef = doc(tourFeedbackRef)

        setDocumentRef(newDocRef)

        await setDoc(newDocRef, feedbackData)
      }
    } catch (error) {
      console.error('Error handling rating in Firestore:', error)
    }
  }

  // Handle feedback submission
  const handleSubmit = async () => {
    if (!documentRef || rating === null) return

    // Skip the update if there's no feedback message
    if (!feedback.trim()) {
      onSubmit?.()
      return
    }

    setIsSubmitting(true)

    try {
      await updateDoc(documentRef, {
        message: feedback.trim(),
      })

      onSubmit?.()
    } catch (error) {
      console.error('Error updating feedback message in Firestore:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        How was this tutorial?
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your feedback helps us improve the tutorial experience
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Rating
          name="tour-rating"
          value={rating}
          onChange={handleRatingChange}
          size="large"
          sx={{ fontSize: '2rem' }}
          data-tour-element="true"
        />
      </Box>

      {showFeedbackField && (
        <Box
          sx={{ mb: 3, animation: 'fadeIn 0.3s ease-in' }}
          data-tour-element="true"
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        >
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Tell us more about your experience (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            variant="outlined"
            sx={{
              mb: 2,
            }}
            autoFocus={false}
            data-tour-element="true"
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
            data-tour-element="true"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </Box>
      )}

      {!showFeedbackField && rating !== null && (
        <Typography variant="body2" color="text.secondary">
          Thanks for your rating! You can add more details above.
        </Typography>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Paper>
  )
}

export default TourFeedback
